import { useState } from 'react';
import { useCurrentUser } from '../../../../../shared/features/session/components/CurrentUserProvider';
import type { JobDetail } from '../../../jobs.api';
import type { useJobManualPrice } from '../hooks/use-job-manual-price';
import { manualPriceActions } from '../policies/manual-price-actions.policy';
import { AddLineToCardModal } from './AddLineToCardModal';
import { ManualPriceBanner } from './ManualPriceBanner';
import { ManualPriceDecisionModal } from './ManualPriceDecisionModal';
import { ManualPriceModal } from './ManualPriceModal';

interface JobManualPricePanelProps {
  job: JobDetail;
  manualPrice: ReturnType<typeof useJobManualPrice>;
}

/**
 * A job the rate card could not price (PRD → Rate cards → Off-card jobs; gitaumoses4/chukua#35): a tag, the
 * price waiting for approval, and the buttons that fit who is looking. The "add to card" offer outlives the
 * alert, because approving clears `pricingRequired`.
 */
export function JobManualPricePanel({ job, manualPrice }: JobManualPricePanelProps) {
  const [isEntering, setIsEntering] = useState(false);
  const [decision, setDecision] = useState<'approve' | 'reject' | null>(null);
  const { state } = useCurrentUser();
  const { request } = manualPrice;
  const { canSubmit, canDecide } = manualPriceActions({
    pricingRequired: job.pricingRequired,
    request,
    userId: state.status === 'authenticated' ? state.user.id : undefined,
    permissions: state.status === 'authenticated' ? state.user.permissions : [],
  });

  async function submitPrice(input: Parameters<typeof manualPrice.submit>[0]) {
    const isSent = await manualPrice.submit(input);
    if (isSent) setIsEntering(false);
    return isSent;
  }

  async function decide(kind: 'approve' | 'reject', reason: string | undefined) {
    const isDecided = kind === 'approve' ? await manualPrice.approve({ reason }) : await manualPrice.reject({ reason: reason ?? '' });
    if (isDecided) setDecision(null);
    return isDecided;
  }

  return (
    <>
      {job.pricingRequired && (
        <ManualPriceBanner
          request={manualPrice.isLoaded ? request : null}
          canSubmit={canSubmit}
          canDecide={canDecide}
          onEnterPrice={() => setIsEntering(true)}
          onDecide={setDecision}
        />
      )}
      <ManualPriceModal
        open={isEntering}
        jobNumber={job.number}
        error={manualPrice.error}
        submitting={manualPrice.submitting}
        onCancel={() => setIsEntering(false)}
        onSubmit={submitPrice}
      />
      <ManualPriceDecisionModal
        decision={decision}
        jobNumber={job.number}
        error={manualPrice.error}
        submitting={manualPrice.submitting}
        onCancel={() => setDecision(null)}
        onSubmit={decide}
      />
      <AddLineToCardModal
        open={manualPrice.offerLine}
        jobNumber={job.number}
        error={manualPrice.error}
        submitting={manualPrice.submitting}
        onAdd={() => manualPrice.proposeLine({})}
        onDismiss={manualPrice.dismissOffer}
      />
    </>
  );
}
